'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import Icon from '@mdi/react'
import { mdiAccountArrowRight, mdiDragVertical } from '@mdi/js'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// lib
import { useNeedsAttentionStore } from '@store/NeedsAttention'
import './next-member-button.css'

/* -----------------------------------------------------------------------------
 * constants
 * -------------------------------------------------------------------------- */

// px from the dot's centre within which the pointer opens it
const OPEN_DISTANCE = 64
// px the pointer may wander past the open button before it closes again
const CLOSE_MARGIN = 32
// px the pointer moves before a press becomes a drag
const DRAG_THRESHOLD = 4
// the open button's height, kept clear of the window's top and bottom
const BUTTON_HEIGHT = 52
const EDGE_MARGIN = 16
// how long the button stays open after Mark as resolved or a tap
const REVEAL_MS = 4000
// how far an arrow key moves the button, as a fraction of the window height
const KEY_STEP = 0.04

const clampCenter = (center: number, viewportHeight: number) =>
  Math.min(
    Math.max(center, EDGE_MARGIN + BUTTON_HEIGHT / 2),
    viewportHeight - EDGE_MARGIN - BUTTON_HEIGHT / 2
  )

/* -----------------------------------------------------------------------------
 * NextMemberButton
 *
 * A dot tucked into the right edge of the chat that opens into a button to the
 * next member who needs attention when the pointer comes near it, and that
 * can be dragged up or down the edge when it covers something.
 * -------------------------------------------------------------------------- */

type NextMemberButtonProps = {
  userId: number
}

function NextMemberButton({ userId }: NextMemberButtonProps) {
  const navigate = useNavigate()
  const nextMember = useNeedsAttentionStore(state => state.nextMember)
  const buttonY = useNeedsAttentionStore(state => state.nextMemberButtonY)

  const buttonRef = useRef<HTMLButtonElement>(null)
  const drag = useRef<{
    pointerId: number
    startY: number
    startCenter: number
    center: number | null
  } | null>(null)
  // the click that ends a drag must not open the member
  const isClickAfterDrag = useRef(false)
  const pressPointerType = useRef('')

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight)
  const [dragCenter, setDragCenter] = useState<number | null>(null)
  const [isNear, setIsNear] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [revealedUntil, setRevealedUntil] = useState(0)

  const isCurrent = nextMember?.forUserId === userId
  const nextUserId = isCurrent ? nextMember.userId : null
  const isSkipped = isCurrent && nextMember.isSkipped
  const revealedAt = isCurrent ? nextMember.revealedAt : undefined
  const isDragging = dragCenter !== null
  const isRevealed = revealedUntil > Date.now()
  const isOpen = isNear || isFocused || isDragging || isRevealed
  const center =
    dragCenter ?? clampCenter(buttonY * viewportHeight, viewportHeight)

  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (revealedAt) {
      setRevealedUntil(revealedAt + REVEAL_MS)
    }
  }, [revealedAt])

  useEffect(() => {
    const wait = revealedUntil - Date.now()
    if (wait <= 0) {
      return
    }
    const timer = setTimeout(() => setRevealedUntil(0), wait)
    return () => clearTimeout(timer)
  }, [revealedUntil])

  // opens near the dot's centre, and closes only once the pointer is clear of
  // the open button, so it does not flicker at the boundary
  useEffect(() => {
    if (!nextUserId) {
      return
    }

    let frame = 0
    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        return
      }
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const button = buttonRef.current
        if (!button) {
          return
        }
        const rect = button.getBoundingClientRect()
        setIsNear(wasNear => {
          if (wasNear) {
            return (
              event.clientX >= rect.left - CLOSE_MARGIN &&
              event.clientX <= rect.right + CLOSE_MARGIN &&
              event.clientY >= rect.top - CLOSE_MARGIN &&
              event.clientY <= rect.bottom + CLOSE_MARGIN
            )
          }
          const visibleRight = Math.min(rect.right, window.innerWidth)
          return (
            Math.hypot(
              event.clientX - (rect.left + visibleRight) / 2,
              event.clientY - (rect.top + rect.bottom) / 2
            ) <= OPEN_DISTANCE
          )
        })
      })
    }
    const onLeave = () => setIsNear(false)

    window.addEventListener('pointermove', onPointerMove, { passive: true })
    document.documentElement.addEventListener('mouseleave', onLeave)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('pointermove', onPointerMove)
      document.documentElement.removeEventListener('mouseleave', onLeave)
    }
  }, [nextUserId])

  if (!nextUserId) {
    return null
  }

  const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) {
      return
    }
    isClickAfterDrag.current = false
    pressPointerType.current = event.pointerType
    drag.current = {
      center: null,
      pointerId: event.pointerId,
      startCenter: center,
      startY: event.clientY
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const current = drag.current
    if (!current || current.pointerId !== event.pointerId) {
      return
    }
    const deltaY = event.clientY - current.startY
    if (current.center === null && Math.abs(deltaY) < DRAG_THRESHOLD) {
      return
    }
    current.center = clampCenter(current.startCenter + deltaY, viewportHeight)
    setDragCenter(current.center)
  }

  const endDrag = () => {
    const current = drag.current
    drag.current = null
    if (current?.center == null) {
      return
    }
    isClickAfterDrag.current = true
    useNeedsAttentionStore
      .getState()
      .setNextMemberButtonY(current.center / viewportHeight)
    setDragCenter(null)
  }

  const onClick = () => {
    if (isClickAfterDrag.current) {
      isClickAfterDrag.current = false
      return
    }
    // a touch has no pointer coming near: the first tap opens the dot. A
    // mouse aimed at it already, even if it has not opened yet
    if (!isOpen && pressPointerType.current !== 'mouse') {
      setRevealedUntil(Date.now() + REVEAL_MS)
      return
    }
    navigate(`/chat/${nextUserId}`)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return
    }
    event.preventDefault()
    const step = event.key === 'ArrowUp' ? -KEY_STEP : KEY_STEP
    useNeedsAttentionStore
      .getState()
      .setNextMemberButtonY(center / viewportHeight + step)
  }

  const className = [
    'next-member-button',
    isOpen && 'next-member-button--open',
    isDragging && 'next-member-button--dragging',
    isRevealed && 'next-member-button--revealed'
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className='next-member-anchor' style={{ top: center }}>
      <button
        ref={buttonRef}
        aria-label={
          isSkipped
            ? `Back to skipped member ${nextUserId}`
            : `Next member who needs attention: member ${nextUserId}`
        }
        className={className}
        onBlur={() => setIsFocused(false)}
        onClick={onClick}
        onFocus={event =>
          setIsFocused(event.currentTarget.matches(':focus-visible'))
        }
        onKeyDown={onKeyDown}
        onPointerCancel={endDrag}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        // the same member the queue bar names
        title={`${isSkipped ? 'Back to' : 'Next:'} member ${nextUserId}`}
        type='button'
      >
        <span className='next-member-icon'>
          <Icon path={mdiAccountArrowRight} size='18px' />
        </span>
        <span className='next-member-labels'>
          <span className='next-member-title'>
            {isSkipped ? 'Skipped member' : 'Next member'}
          </span>
          <span className='next-member-subtitle'>
            {isSkipped ? `back to ${nextUserId}` : 'needs attention'}
          </span>
        </span>
        <span className='next-member-grip' title='Drag up or down to move'>
          <Icon path={mdiDragVertical} size='16px' />
        </span>
      </button>
    </div>
  )
}

export default NextMemberButton
